"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.OracleType = exports.PriceStatus = exports.Exchange = exports.STATE_SEED = void 0;
const network_1 = require("./network");
const exchange_1 = require("./idl/exchange");
const anchor_1 = require("@project-serum/anchor");
const utils_1 = require("./utils");
const spl_token_1 = require("@solana/spl-token");
const web3_js_1 = require("@solana/web3.js");
exports.STATE_SEED = 'statev1';
class Exchange {
    constructor(connection, network, wallet, exchangeAuthority = web3_js_1.PublicKey.default, programId = web3_js_1.PublicKey.default, opts) {
        this.stateAddress = web3_js_1.PublicKey.default;
        this.assetsList = {};
        this.state = {};
        this.connection = connection;
        this.network = network;
        this.wallet = wallet;
        this.opts = opts;
        const provider = new anchor_1.Provider(connection, wallet, opts || anchor_1.Provider.defaultOptions());
        switch (network) {
            case network_1.Network.LOCAL:
                this.programId = programId;
                this.exchangeAuthority = exchangeAuthority;
                this.program = new anchor_1.Program(exchange_1.IDL, this.programId, provider);
                break;
            case network_1.Network.DEV:
                this.programId = network_1.DEV_NET.exchange;
                this.exchangeAuthority = network_1.DEV_NET.exchangeAuthority;
                this.program = new anchor_1.Program(exchange_1.IDL, this.programId, provider);
                break;
            case network_1.Network.TEST:
                this.programId = network_1.TEST_NET.exchange;
                this.exchangeAuthority = network_1.TEST_NET.exchangeAuthority;
                this.program = new anchor_1.Program(exchange_1.IDL, this.programId, provider);
                break;
            case network_1.Network.MAIN:
                this.programId = network_1.MAIN_NET.exchange;
                this.exchangeAuthority = network_1.MAIN_NET.exchangeAuthority;
                this.program = new anchor_1.Program(exchange_1.IDL, this.programId, provider);
                break;
            default:
                throw new Error('Not supported');
        }
    }
    static build(connection, network, wallet, exchangeAuthority, programId, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            const instance = new Exchange(connection, network, wallet, exchangeAuthority, programId, opts);
            const [stateAddress, _] = yield web3_js_1.PublicKey.findProgramAddress([Buffer.from(anchor_1.utils.bytes.utf8.encode(exports.STATE_SEED))], instance.program.programId);
            instance.stateAddress = stateAddress;
            return instance;
        });
    }
    onStateChange(fn) {
        this.program.account.state.subscribe(this.stateAddress).on('change', (state) => {
            fn(state);
        });
    }
    onAccountChange(address, fn) {
        this.program.account.exchangeAccount
            .subscribe(address, 'singleGossip')
            .on('change', (account) => {
            fn(account);
        });
    }
    onVaultChange(vault, fn) {
        this.program.account.vault.subscribe(vault, 'recent').on('change', (account) => {
            fn(account);
        });
    }
    onVaultEntryChange(vaultEntry, fn) {
        this.program.account.vaultEntry
            .subscribe(vaultEntry, 'recent')
            .on('change', (account) => {
            fn(account);
        });
    }
    init({ admin, nonce, amountPerRound, stakingRoundLength, stakingFundAccount, exchangeAuthority }) {
        return __awaiter(this, void 0, void 0, function* () {
            const [stateAddress, bump] = yield web3_js_1.PublicKey.findProgramAddress([Buffer.from(anchor_1.utils.bytes.utf8.encode(exports.STATE_SEED))], this.program.programId);
            yield this.program.rpc.init(bump, nonce, stakingRoundLength, amountPerRound, {
                accounts: {
                    state: stateAddress,
                    admin: admin,
                    stakingFundAccount: stakingFundAccount,
                    payer: this.wallet.publicKey,
                    exchangeAuthority: exchangeAuthority,
                    rent: web3_js_1.SYSVAR_RENT_PUBKEY,
                    systemProgram: web3_js_1.SystemProgram.programId
                }
            });
            this.stateAddress = stateAddress;
        });
    }
    setAssetsList({ exchangeAdmin, assetsList }) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.program.rpc.setAssetsList({
                accounts: {
                    assetsList: assetsList,
                    state: this.stateAddress,
                    admin: exchangeAdmin.publicKey
                },
                signers: [exchangeAdmin]
            });
        });
    }
    setAssetsListInstruction(assetsList) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.program.instruction.setAssetsList({
                accounts: {
                    assetsList,
                    state: this.stateAddress,
                    admin: this.state.admin
                }
            }));
        });
    }
    getState() {
        return __awaiter(this, void 0, void 0, function* () {
            const state = (yield this.program.account.state.fetch(this.stateAddress));
            // need to add hooks on change
            this.state = state;
            this.assetsList = yield this.getAssetsList(this.state.assetsList);
            return state;
        });
    }
    getExchangeAccount(exchangeAccount) {
        return __awaiter(this, void 0, void 0, function* () {
            const account = (yield this.program.account.exchangeAccount.fetch(exchangeAccount));
            account.collaterals = account.collaterals.slice(0, account.head);
            return account;
        });
    }
    getSettlementAccountForSynthetic(synthetic) {
        return __awaiter(this, void 0, void 0, function* () {
            const [settlement, bump] = yield web3_js_1.PublicKey.findProgramAddress([Buffer.from(anchor_1.utils.bytes.utf8.encode('settlement')), synthetic.toBuffer()], this.program.programId);
            const account = (yield this.program.account.settlement.fetch(settlement));
            return account;
        });
    }
    getVaultAddress(synthetic, collateral, vaultType) {
        return __awaiter(this, void 0, void 0, function* () {
            const vaultTypeBuffer = Buffer.alloc(1);
            vaultTypeBuffer.writeUInt8(vaultType);
            const [vaultAddress, bump] = yield web3_js_1.PublicKey.findProgramAddress([
                Buffer.from(anchor_1.utils.bytes.utf8.encode('vaultv1')),
                synthetic.toBuffer(),
                collateral.toBuffer(),
                vaultTypeBuffer
            ], this.program.programId);
            return { vaultAddress, bump };
        });
    }
    getVaultEntryAddress(synthetic, collateral, vaultType, owner) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            const [vaultEntryAddress, bump] = yield web3_js_1.PublicKey.findProgramAddress([
                Buffer.from(anchor_1.utils.bytes.utf8.encode('vault_entryv1')),
                owner.toBuffer(),
                vaultAddress.toBuffer()
            ], this.program.programId);
            return {
                vaultEntryAddress,
                bump
            };
        });
    }
    getVaultForPair(synthetic, collateral, vaultType) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            const account = (yield this.program.account.vault.fetch(vaultAddress));
            return account;
        });
    }
    getVaultEntryForOwner(synthetic, collateral, owner, vaultType) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultEntryAddress } = yield this.getVaultEntryAddress(synthetic, collateral, vaultType, owner);
            const account = (yield this.program.account.vaultEntry.fetch(vaultEntryAddress));
            return account;
        });
    }
    getUserCollateralBalance(exchangeAccount) {
        return __awaiter(this, void 0, void 0, function* () {
            const userAccount = (yield this.program.account.exchangeAccount.fetch(exchangeAccount));
            const snyCollateral = this.assetsList.collaterals[0];
            const collateralEntry = userAccount.collaterals.find((entry) => entry.collateralAddress.equals(snyCollateral.collateralAddress));
            if (collateralEntry) {
                return collateralEntry.amount;
            }
            else {
                return new anchor_1.BN(0);
            }
        });
    }
    getUserDebtBalance(exchangeAccount) {
        return __awaiter(this, void 0, void 0, function* () {
            const userAccount = (yield this.program.account.exchangeAccount.fetch(exchangeAccount));
            if (userAccount.debtShares.eq(new anchor_1.BN(0))) {
                return new anchor_1.BN(0);
            }
            const state = yield this.getState();
            const debt = (0, utils_1.calculateDebt)(this.assetsList);
            return userAccount.debtShares.mul(debt).div(state.debtShares);
        });
    }
    createExchangeAccount(owner) {
        return __awaiter(this, void 0, void 0, function* () {
            const [account, bump] = yield web3_js_1.PublicKey.findProgramAddress([Buffer.from(anchor_1.utils.bytes.utf8.encode('accountv1')), owner.toBuffer()], this.program.programId);
            yield this.program.rpc.createExchangeAccount(bump, {
                accounts: {
                    exchangeAccount: account,
                    rent: web3_js_1.SYSVAR_RENT_PUBKEY,
                    admin: owner,
                    payer: this.wallet.publicKey,
                    systemProgram: web3_js_1.SystemProgram.programId
                }
            });
            return account;
        });
    }
    createSwaplineInstruction({ collateral, collateralReserve, synthetic, limit }) {
        return __awaiter(this, void 0, void 0, function* () {
            const [swaplineAddress, bump] = yield web3_js_1.PublicKey.findProgramAddress([
                Buffer.from(anchor_1.utils.bytes.utf8.encode('swaplinev1')),
                synthetic.toBuffer(),
                collateral.toBuffer()
            ], this.program.programId);
            const ix = yield this.program.instruction.createSwapline(bump, limit, {
                accounts: {
                    state: this.stateAddress,
                    swapline: swaplineAddress,
                    synthetic: synthetic,
                    collateral: collateral,
                    assetsList: this.state.assetsList,
                    collateralReserve: collateralReserve,
                    admin: this.state.admin,
                    rent: web3_js_1.SYSVAR_RENT_PUBKEY,
                    systemProgram: web3_js_1.SystemProgram.programId
                }
            });
            return { swaplineAddress, ix };
        });
    }
    withdrawSwaplineFee({ collateral, synthetic, to, amount }) {
        return __awaiter(this, void 0, void 0, function* () {
            const [swaplineAddress, bump] = yield web3_js_1.PublicKey.findProgramAddress([
                Buffer.from(anchor_1.utils.bytes.utf8.encode('swaplinev1')),
                synthetic.toBuffer(),
                collateral.toBuffer()
            ], this.program.programId);
            const swapline = yield this.getSwapline(swaplineAddress);
            const ix = yield this.program.instruction.withdrawSwaplineFee(amount, {
                accounts: {
                    state: this.stateAddress,
                    swapline: swaplineAddress,
                    synthetic: synthetic,
                    collateral: collateral,
                    to: to,
                    collateralReserve: swapline.collateralReserve,
                    admin: this.state.admin,
                    exchangeAuthority: this.exchangeAuthority,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID
                }
            });
            return ix;
        });
    }
    setHaltedSwapline({ collateral, synthetic, halted }) {
        return __awaiter(this, void 0, void 0, function* () {
            const [swaplineAddress, bump] = yield web3_js_1.PublicKey.findProgramAddress([
                Buffer.from(anchor_1.utils.bytes.utf8.encode('swaplinev1')),
                synthetic.toBuffer(),
                collateral.toBuffer()
            ], this.program.programId);
            const ix = (yield this.program.instruction.setHaltedSwapline(halted, {
                accounts: {
                    state: this.stateAddress,
                    swapline: swaplineAddress,
                    synthetic: synthetic,
                    collateral: collateral,
                    admin: this.state.admin
                }
            }));
            return ix;
        });
    }
    nativeToSynthetic({ collateral, synthetic, signer, userCollateralAccount, userSyntheticAccount, amount }) {
        return __awaiter(this, void 0, void 0, function* () {
            const [swaplineAddress, bump] = yield web3_js_1.PublicKey.findProgramAddress([
                Buffer.from(anchor_1.utils.bytes.utf8.encode('swaplinev1')),
                synthetic.toBuffer(),
                collateral.toBuffer()
            ], this.program.programId);
            const swapline = yield this.getSwapline(swaplineAddress);
            const ix = yield this.program.instruction.nativeToSynthetic(amount, {
                accounts: {
                    state: this.stateAddress,
                    swapline: swaplineAddress,
                    synthetic: synthetic,
                    collateral: collateral,
                    userCollateralAccount: userCollateralAccount,
                    userSyntheticAccount: userSyntheticAccount,
                    assetsList: this.state.assetsList,
                    collateralReserve: swapline.collateralReserve,
                    signer: signer,
                    exchangeAuthority: this.exchangeAuthority,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID
                }
            });
            return ix;
        });
    }
    syntheticToNative({ collateral, synthetic, signer, userCollateralAccount, userSyntheticAccount, amount }) {
        return __awaiter(this, void 0, void 0, function* () {
            const [swaplineAddress, bump] = yield web3_js_1.PublicKey.findProgramAddress([
                Buffer.from(anchor_1.utils.bytes.utf8.encode('swaplinev1')),
                synthetic.toBuffer(),
                collateral.toBuffer()
            ], this.program.programId);
            const swapline = yield this.getSwapline(swaplineAddress);
            const ix = yield this.program.instruction.syntheticToNative(amount, {
                accounts: {
                    state: this.stateAddress,
                    swapline: swaplineAddress,
                    synthetic: synthetic,
                    collateral: collateral,
                    userCollateralAccount: userCollateralAccount,
                    userSyntheticAccount: userSyntheticAccount,
                    assetsList: this.state.assetsList,
                    collateralReserve: swapline.collateralReserve,
                    signer: signer,
                    exchangeAuthority: this.exchangeAuthority,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID
                }
            });
            return ix;
        });
    }
    getSwapline(swapline) {
        return __awaiter(this, void 0, void 0, function* () {
            const swaplineData = (yield this.program.account.swapline.fetch(swapline));
            return swaplineData;
        });
    }
    getSwaplineAddress(synthetic, collateral) {
        return __awaiter(this, void 0, void 0, function* () {
            const [swaplineAddress, bump] = yield web3_js_1.PublicKey.findProgramAddress([
                Buffer.from(anchor_1.utils.bytes.utf8.encode('swaplinev1')),
                synthetic.toBuffer(),
                collateral.toBuffer()
            ], this.program.programId);
            return { swaplineAddress, bump };
        });
    }
    createExchangeAccountInstruction(owner) {
        return __awaiter(this, void 0, void 0, function* () {
            const [account, bump] = yield web3_js_1.PublicKey.findProgramAddress([Buffer.from(anchor_1.utils.bytes.utf8.encode('accountv1')), owner.toBuffer()], this.program.programId);
            const ix = (yield this.program.instruction.createExchangeAccount(bump, {
                accounts: {
                    exchangeAccount: account,
                    rent: web3_js_1.SYSVAR_RENT_PUBKEY,
                    admin: owner,
                    payer: this.wallet.publicKey,
                    systemProgram: web3_js_1.SystemProgram.programId
                }
            }));
            return { account, ix };
        });
    }
    getExchangeAccountAddress(owner) {
        return __awaiter(this, void 0, void 0, function* () {
            const [account, bump] = yield web3_js_1.PublicKey.findProgramAddress([Buffer.from(anchor_1.utils.bytes.utf8.encode('accountv1')), owner.toBuffer()], this.program.programId);
            return account;
        });
    }
    depositInstruction({ amount, exchangeAccount, userCollateralAccount, owner, reserveAddress }) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.program.instruction.deposit(amount, {
                accounts: {
                    state: this.stateAddress,
                    owner: owner,
                    exchangeAccount: exchangeAccount,
                    userCollateralAccount: userCollateralAccount,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                    exchangeAuthority: this.exchangeAuthority,
                    reserveAddress: reserveAddress,
                    assetsList: this.state.assetsList
                }
            }));
        });
    }
    withdrawInstruction({ amount, exchangeAccount, owner, userCollateralAccount, reserveAccount }) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.withdraw(amount, {
                accounts: {
                    state: this.stateAddress,
                    assetsList: this.state.assetsList,
                    exchangeAuthority: this.exchangeAuthority,
                    reserveAccount,
                    userCollateralAccount,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                    owner: owner,
                    exchangeAccount: exchangeAccount
                }
            });
        });
    }
    mintInstruction({ amount, exchangeAccount, owner, to }) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.mint(amount, {
                accounts: {
                    state: this.stateAddress,
                    exchangeAuthority: this.exchangeAuthority,
                    usdToken: this.assetsList.synthetics[0].assetAddress,
                    to: to,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                    exchangeAccount: exchangeAccount,
                    owner: owner,
                    assetsList: this.state.assetsList
                }
            });
        });
    }
    swapInstruction({ amount, owner, tokenFor, tokenIn, userTokenAccountFor, userTokenAccountIn, exchangeAccount }) {
        return __awaiter(this, void 0, void 0, function* () {
            const remainingAccounts = exchangeAccount
                ? [{ pubkey: exchangeAccount, isWritable: false, isSigner: false }]
                : [];
            return this.program.instruction.swap(amount, {
                remainingAccounts,
                accounts: {
                    state: this.stateAddress,
                    exchangeAuthority: this.exchangeAuthority,
                    tokenFor: tokenFor,
                    tokenIn: tokenIn,
                    userTokenAccountFor: userTokenAccountFor,
                    userTokenAccountIn: userTokenAccountIn,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                    owner: owner,
                    assetsList: this.state.assetsList
                }
            });
        });
    }
    liquidateInstruction({ exchangeAccount, signer, liquidationFund, liquidatorCollateralAccount, liquidatorUsdAccount, reserveAccount, amount }) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.liquidate(amount, {
                accounts: {
                    state: this.stateAddress,
                    exchangeAuthority: this.exchangeAuthority,
                    assetsList: this.state.assetsList,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                    usdToken: this.assetsList.synthetics[0].assetAddress,
                    liquidatorUsdAccount: liquidatorUsdAccount,
                    liquidatorCollateralAccount: liquidatorCollateralAccount,
                    exchangeAccount: exchangeAccount,
                    signer: signer,
                    liquidationFund: liquidationFund,
                    reserveAccount: reserveAccount
                }
            });
        });
    }
    burnInstruction({ amount, exchangeAccount, owner, userTokenAccountBurn }) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.burn(amount, {
                accounts: {
                    state: this.stateAddress,
                    exchangeAuthority: this.exchangeAuthority,
                    usdToken: this.assetsList.synthetics[0].assetAddress,
                    userTokenAccountBurn: userTokenAccountBurn,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                    exchangeAccount: exchangeAccount,
                    owner: owner,
                    assetsList: this.state.assetsList
                }
            });
        });
    }
    claimRewardsInstruction(exchangeAccount) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.claimRewards({
                accounts: {
                    state: this.stateAddress,
                    exchangeAccount: exchangeAccount
                }
            });
        });
    }
    withdrawRewardsInstruction({ exchangeAccount, owner, userTokenAccount }) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.withdrawRewards({
                accounts: {
                    state: this.stateAddress,
                    exchangeAccount: exchangeAccount,
                    exchangeAuthority: this.exchangeAuthority,
                    owner: owner,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                    userTokenAccount: userTokenAccount,
                    stakingFundAccount: this.state.staking.fundAccount
                }
            });
        });
    }
    checkAccountInstruction(exchangeAccount) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.checkAccountCollateralization({
                accounts: {
                    state: this.stateAddress,
                    exchangeAccount: exchangeAccount,
                    assetsList: this.state.assetsList
                }
            });
        });
    }
    setLiquidationBufferInstruction(newLiquidationBuffer) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.setLiquidationBuffer(newLiquidationBuffer, {
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin
                }
            });
        });
    }
    setLiquidationRateInstruction(newLiquidationRate) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.setLiquidationRate(newLiquidationRate, {
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin
                }
            });
        });
    }
    setFeeInstruction(newFee) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.setFee(newFee, {
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin
                }
            });
        });
    }
    setMaxDelayInstruction(newMaxDelay) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.setMaxDelay(newMaxDelay, {
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin
                }
            });
        });
    }
    setHaltedInstruction(halted) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.setHalted(halted, {
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin
                }
            });
        });
    }
    setHealthFactorInstruction(percentage) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.setHealthFactor(percentage, {
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin
                }
            });
        });
    }
    setStakingAmountPerRound(amount) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.setStakingAmountPerRound(amount, {
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin
                }
            });
        });
    }
    setStakingRoundLength(length) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.setStakingRoundLength(length, {
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin
                }
            });
        });
    }
    setCollateralRatio(collateralAddress, newRatio) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.setCollateralRatio(newRatio, {
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin,
                    assetsList: this.state.assetsList,
                    collateralAddress: collateralAddress
                }
            });
        });
    }
    setMaxCollateral(collateralAddress, newMaxCollateral) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.setMaxCollateral(newMaxCollateral, {
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin,
                    assetsList: this.state.assetsList,
                    collateralAddress: collateralAddress
                }
            });
        });
    }
    setAdmin(newAdmin) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.setAdmin({
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin,
                    newAdmin: newAdmin
                }
            });
        });
    }
    setSettlementSlotInstruction(syntheticAddress, newSettlementSlot) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.setSettlementSlot(newSettlementSlot, {
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin,
                    assetsList: this.state.assetsList,
                    syntheticAddress: syntheticAddress
                }
            });
        });
    }
    settleSynthetic({ payer, settlementReserve, tokenToSettle }) {
        return __awaiter(this, void 0, void 0, function* () {
            const assetsList = yield this.getAssetsList(this.state.assetsList);
            const synthetic = assetsList.synthetics.find((s) => s.assetAddress.equals(tokenToSettle));
            const feedAddress = assetsList.assets[synthetic.assetIndex].feedAddress;
            const priceFeed = { pubkey: feedAddress, isWritable: false, isSigner: false };
            const oracleUpdateIx = (yield this.program.instruction.setAssetsPrices({
                remainingAccounts: [priceFeed],
                accounts: {
                    assetsList: this.state.assetsList
                }
            }));
            const [settlement, bump] = yield web3_js_1.PublicKey.findProgramAddress([Buffer.from(anchor_1.utils.bytes.utf8.encode('settlement')), tokenToSettle.toBuffer()], this.program.programId);
            const settleIx = this.program.instruction.settleSynthetic(bump, {
                accounts: {
                    settlement: settlement,
                    state: this.stateAddress,
                    assetsList: this.state.assetsList,
                    payer: payer,
                    tokenToSettle: tokenToSettle,
                    settlementReserve: settlementReserve,
                    usdToken: this.assetsList.synthetics[0].assetAddress,
                    rent: web3_js_1.SYSVAR_RENT_PUBKEY,
                    exchangeAuthority: this.exchangeAuthority,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                    systemProgram: web3_js_1.SystemProgram.programId
                }
            });
            return { oracleUpdateIx, settleIx, settlement };
        });
    }
    swapSettledSyntheticInstruction({ tokenToSettle, userSettledTokenAccount, userUsdAccount, amount, signer }) {
        return __awaiter(this, void 0, void 0, function* () {
            const settlement = yield this.getSettlementAccountForSynthetic(tokenToSettle);
            const [settlementAddress, bump] = yield web3_js_1.PublicKey.findProgramAddress([Buffer.from(anchor_1.utils.bytes.utf8.encode('settlement')), tokenToSettle.toBuffer()], this.program.programId);
            const ix = this.program.instruction.swapSettledSynthetic(amount, {
                accounts: {
                    settlement: settlementAddress,
                    state: this.stateAddress,
                    tokenToSettle: tokenToSettle,
                    userSettledTokenAccount: userSettledTokenAccount,
                    userUsdAccount: userUsdAccount,
                    settlementReserve: settlement.reserveAddress,
                    usdToken: this.assetsList.synthetics[0].assetAddress,
                    exchangeAuthority: this.exchangeAuthority,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                    signer: signer
                }
            });
            return ix;
        });
    }
    setSwapTaxRatioInstruction(swapTaxRatio) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.setSwapTaxRatio(swapTaxRatio, {
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin
                }
            });
        });
    }
    setDebtInterestRateInstruction(debtInterestRate) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.setDebtInterestRate(debtInterestRate, {
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin
                }
            });
        });
    }
    processOperations(txs) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const blockhash = yield this.connection.getRecentBlockhash(((_a = this.opts) === null || _a === void 0 ? void 0 : _a.commitment) || anchor_1.Provider.defaultOptions().commitment);
            txs.forEach((tx) => {
                tx.feePayer = this.wallet.publicKey;
                tx.recentBlockhash = blockhash.blockhash;
            });
            const signedTx = yield this.wallet.signAllTransactions(txs);
            return signedTx;
        });
    }
    updatePricesAndSend(ixs, signers, split) {
        return __awaiter(this, void 0, void 0, function* () {
            const updateIx = yield this.updatePricesInstruction(this.state.assetsList);
            if (!split) {
                let tx = new web3_js_1.Transaction().add(updateIx);
                ixs.forEach((ix) => tx.add(ix));
                const txs = yield this.processOperations([tx]);
                if (signers)
                    txs[0].partialSign(...signers);
                return (0, web3_js_1.sendAndConfirmRawTransaction)(this.connection, txs[0].serialize());
            }
            else {
                let tx = new web3_js_1.Transaction();
                ixs.forEach((ix) => tx.add(ix));
                const txs = yield this.processOperations([new web3_js_1.Transaction().add(updateIx), tx]);
                if (signers)
                    txs[1].partialSign(...signers);
                (0, web3_js_1.sendAndConfirmRawTransaction)(this.connection, txs[0].serialize(), {
                    skipPreflight: true
                });
                yield (0, utils_1.sleep)(100);
                return (0, web3_js_1.sendAndConfirmRawTransaction)(this.connection, txs[1].serialize(), {
                    skipPreflight: true
                });
            }
        });
    }
    checkAccount(exchangeAccount) {
        return __awaiter(this, void 0, void 0, function* () {
            const updateIx = yield this.updatePricesInstruction(this.state.assetsList);
            const checkIx = yield this.checkAccountInstruction(exchangeAccount);
            const checkTx = new web3_js_1.Transaction().add(updateIx).add(checkIx);
            const txs = yield this.processOperations([checkTx]);
            return (0, web3_js_1.sendAndConfirmRawTransaction)(this.connection, txs[0].serialize());
        });
    }
    liquidate({ exchangeAccount, signer, signers, liquidationFund, liquidatorCollateralAccount, liquidatorUsdAccount, reserveAccount, amount }) {
        return __awaiter(this, void 0, void 0, function* () {
            const updateIx = yield this.updatePricesInstruction(this.state.assetsList);
            const liquidateIx = yield this.liquidateInstruction({
                exchangeAccount,
                signer,
                liquidationFund,
                liquidatorCollateralAccount,
                liquidatorUsdAccount,
                reserveAccount,
                amount
            });
            const approveIx = yield spl_token_1.Token.createApproveInstruction(spl_token_1.TOKEN_PROGRAM_ID, liquidatorUsdAccount, this.exchangeAuthority, signer, [], (0, utils_1.tou64)(amount));
            const liquidateTx = new web3_js_1.Transaction().add(updateIx).add(approveIx).add(liquidateIx);
            const txs = yield this.processOperations([liquidateTx]);
            signers ? txs[0].partialSign(...signers) : null;
            return (0, web3_js_1.sendAndConfirmRawTransaction)(this.connection, txs[0].serialize());
        });
    }
    swap({ amount, owner, tokenFor, tokenIn, userTokenAccountFor, userTokenAccountIn, signers, exchangeAccount }) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.getState();
            const swapIx = yield this.swapInstruction({
                amount,
                exchangeAccount,
                owner,
                tokenFor,
                tokenIn,
                userTokenAccountFor,
                userTokenAccountIn
            });
            const approveIx = yield spl_token_1.Token.createApproveInstruction(spl_token_1.TOKEN_PROGRAM_ID, userTokenAccountIn, this.exchangeAuthority, owner, [], (0, utils_1.tou64)(amount));
            return this.updatePricesAndSend([approveIx, swapIx], signers, this.assetsList.headAssets >= 20);
        });
    }
    burn({ amount, exchangeAccount, owner, userTokenAccountBurn, signers }) {
        return __awaiter(this, void 0, void 0, function* () {
            const updateIx = yield this.updatePricesInstruction(this.state.assetsList);
            const burnIx = yield this.burnInstruction({
                amount,
                exchangeAccount,
                owner,
                userTokenAccountBurn
            });
            const approveIx = yield spl_token_1.Token.createApproveInstruction(spl_token_1.TOKEN_PROGRAM_ID, userTokenAccountBurn, this.exchangeAuthority, owner, [], (0, utils_1.tou64)(amount));
            const burnTx = new web3_js_1.Transaction().add(updateIx).add(approveIx).add(burnIx);
            const txs = yield this.processOperations([burnTx]);
            signers ? txs[0].partialSign(...signers) : null;
            return (0, web3_js_1.sendAndConfirmRawTransaction)(this.connection, txs[0].serialize());
        });
    }
    mint({ amount, exchangeAccount, owner, to, signers }) {
        return __awaiter(this, void 0, void 0, function* () {
            const mintIx = yield this.mintInstruction({
                amount,
                exchangeAccount,
                owner,
                to
            });
            yield this.getState();
            return yield this.updatePricesAndSend([mintIx], signers, this.assetsList.headAssets >= 20);
        });
    }
    deposit({ amount, exchangeAccount, owner, userCollateralAccount, reserveAccount, collateralToken, exchangeAuthority, signers }) {
        return __awaiter(this, void 0, void 0, function* () {
            const depositIx = yield this.depositInstruction({
                amount,
                exchangeAccount,
                userCollateralAccount,
                owner,
                reserveAddress: reserveAccount
            });
            const approveIx = spl_token_1.Token.createApproveInstruction(collateralToken.programId, userCollateralAccount, exchangeAuthority, owner, [], (0, utils_1.tou64)(amount));
            yield (0, utils_1.signAndSend)(new web3_js_1.Transaction().add(approveIx).add(depositIx), signers, this.connection);
        });
    }
    withdraw({ amount, exchangeAccount, owner, userCollateralAccount, signers, reserveAccount }) {
        return __awaiter(this, void 0, void 0, function* () {
            const withdrawIx = yield this.withdrawInstruction({
                reserveAccount,
                amount,
                exchangeAccount,
                owner,
                userCollateralAccount
            });
            yield this.getState();
            return this.updatePricesAndSend([withdrawIx], signers, this.assetsList.headAssets >= 20);
        });
    }
    withdrawRewards({ exchangeAccount, owner, signers, userTokenAccount }) {
        return __awaiter(this, void 0, void 0, function* () {
            const withdrawIx = yield this.withdrawRewardsInstruction({
                userTokenAccount,
                exchangeAccount,
                owner
            });
            const withdrawTx = new web3_js_1.Transaction().add(withdrawIx);
            const txs = yield this.processOperations([withdrawTx]);
            signers ? txs[0].partialSign(...signers) : null;
            return (0, web3_js_1.sendAndConfirmRawTransaction)(this.connection, txs[0].serialize());
        });
    }
    claimRewards(exchangeAccount) {
        return __awaiter(this, void 0, void 0, function* () {
            const claimRewardsIx = yield this.claimRewardsInstruction(exchangeAccount);
            const tx = new web3_js_1.Transaction().add(claimRewardsIx);
            const txs = yield this.processOperations([tx]);
            return (0, web3_js_1.sendAndConfirmRawTransaction)(this.connection, txs[0].serialize());
        });
    }
    // Asset list
    getAssetsList(assetsList) {
        return __awaiter(this, void 0, void 0, function* () {
            const assetList = (yield this.program.account.assetsList.fetch(assetsList));
            assetList.assets = assetList.assets.slice(0, assetList.headAssets);
            assetList.collaterals = assetList.collaterals.slice(0, assetList.headCollaterals);
            assetList.synthetics = assetList.synthetics.slice(0, assetList.headSynthetics);
            return assetList;
        });
    }
    onAssetsListChange(address, fn) {
        this.program.account.assetsList
            .subscribe(address, 'singleGossip')
            .on('change', (list) => {
            fn(list);
        });
    }
    setPriceFeedInstruction({ assetsList, priceFeed, oldPriceFeed }) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.program.instruction.setPriceFeed(oldPriceFeed, {
                accounts: {
                    state: this.stateAddress,
                    signer: this.state.admin,
                    assetsList: assetsList,
                    priceFeed: priceFeed
                }
            }));
        });
    }
    setLiquidationPenaltiesInstruction({ penaltyToExchange, penaltyToLiquidator }) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.program.instruction.setLiquidationPenalties(penaltyToExchange, penaltyToLiquidator, {
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin
                }
            }));
        });
    }
    addSyntheticInstruction({ assetsList, assetAddress, priceFeed, maxSupply }) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.program.instruction.addSynthetic(maxSupply, {
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin,
                    assetsList,
                    assetAddress: assetAddress,
                    feedAddress: priceFeed
                }
            }));
        });
    }
    initializeAssetsList({ admin, collateralToken, collateralTokenFeed, usdToken, snyLiquidationFund, snyReserve }) {
        return __awaiter(this, void 0, void 0, function* () {
            const assetListAccount = web3_js_1.Keypair.generate();
            yield this.program.rpc.createList({
                accounts: {
                    collateralToken,
                    collateralTokenFeed,
                    usdToken,
                    admin: admin.publicKey,
                    state: this.stateAddress,
                    assetsList: assetListAccount.publicKey,
                    snyReserve: snyReserve,
                    snyLiquidationFund: snyLiquidationFund,
                    exchangeAuthority: this.exchangeAuthority,
                    rent: web3_js_1.SYSVAR_RENT_PUBKEY
                },
                signers: [admin, assetListAccount],
                instructions: [yield this.program.account.assetsList.createInstruction(assetListAccount)]
            });
            return assetListAccount.publicKey;
        });
    }
    setAssetMaxSupply({ assetsList, exchangeAdmin, assetAddress, newMaxSupply }) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.rpc.setMaxSupply(assetAddress, newMaxSupply, {
                accounts: {
                    state: this.stateAddress,
                    signer: exchangeAdmin.publicKey,
                    assetsList: assetsList
                },
                signers: [exchangeAdmin]
            });
        });
    }
    setAssetMaxSupplyInstruction({ assetAddress, newMaxSupply }) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.program.instruction.setMaxSupply(assetAddress, newMaxSupply, {
                accounts: {
                    state: this.stateAddress,
                    signer: this.state.admin,
                    assetsList: this.state.assetsList
                }
            });
        });
    }
    addNewAssetInstruction({ assetsList, assetFeedAddress }) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.program.instruction.addNewAsset(assetFeedAddress, {
                accounts: {
                    state: this.stateAddress,
                    signer: this.state.admin,
                    assetsList
                }
            }));
        });
    }
    withdrawSwapTaxInstruction({ amount, to }) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.program.instruction.withdrawSwapTax(amount, {
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin,
                    exchangeAuthority: this.exchangeAuthority,
                    assetsList: this.state.assetsList,
                    usdToken: this.assetsList.synthetics[0].assetAddress,
                    to: to,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID
                }
            }));
        });
    }
    withdrawAccumulatedDebtInterestInstruction({ amount, to }) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.program.instruction.withdrawAccumulatedDebtInterest(amount, {
                accounts: {
                    state: this.stateAddress,
                    admin: this.state.admin,
                    exchangeAuthority: this.exchangeAuthority,
                    assetsList: this.state.assetsList,
                    usdToken: this.assetsList.synthetics[0].assetAddress,
                    to: to,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID
                }
            }));
        });
    }
    addCollateralInstruction({ assetsList, assetAddress, liquidationFund, reserveAccount, feedAddress, collateralRatio, reserveBalance, maxCollateral }) {
        return __awaiter(this, void 0, void 0, function* () {
            return (yield this.program.instruction.addCollateral(reserveBalance, maxCollateral, collateralRatio, {
                accounts: {
                    admin: this.state.admin,
                    state: this.stateAddress,
                    assetsList,
                    assetAddress,
                    liquidationFund,
                    feedAddress,
                    reserveAccount
                }
            }));
        });
    }
    createVaultInstruction({ synthetic, collateral, collateralReserve, collateralPriceFeed, liquidationFund, openFee, debtInterestRate, collateralRatio, maxBorrow, liquidationThreshold, liquidationPenaltyLiquidator, liquidationPenaltyExchange, liquidationRatio, oracleType, vaultType }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress, bump } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            const ix = this.program.instruction.createVault(bump, vaultType, openFee, debtInterestRate, collateralRatio, maxBorrow, liquidationThreshold, liquidationPenaltyLiquidator, liquidationPenaltyExchange, liquidationRatio, oracleType, {
                accounts: {
                    vault: vaultAddress,
                    admin: this.state.admin,
                    assetsList: this.state.assetsList,
                    state: this.stateAddress,
                    collateralReserve: collateralReserve,
                    synthetic: synthetic,
                    collateral: collateral,
                    liquidationFund,
                    collateralPriceFeed,
                    rent: web3_js_1.SYSVAR_RENT_PUBKEY,
                    systemProgram: web3_js_1.SystemProgram.programId
                }
            });
            return { ix, vaultAddress };
        });
    }
    createVaultEntryInstruction({ owner, synthetic, collateral, vaultType }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            const { vaultEntryAddress, bump } = yield this.getVaultEntryAddress(synthetic, collateral, vaultType, owner);
            const ix = this.program.instruction.createVaultEntry(bump, {
                accounts: {
                    owner: owner,
                    vaultEntry: vaultEntryAddress,
                    vault: vaultAddress,
                    state: this.stateAddress,
                    assetsList: this.state.assetsList,
                    synthetic: synthetic,
                    collateral: collateral,
                    rent: web3_js_1.SYSVAR_RENT_PUBKEY,
                    systemProgram: web3_js_1.SystemProgram.programId
                }
            });
            return { ix, vaultEntryAddress };
        });
    }
    vaultDepositInstruction({ owner, synthetic, collateral, userCollateralAccount, reserveAddress, amount, vaultType }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            const { vaultEntryAddress } = yield this.getVaultEntryAddress(synthetic, collateral, vaultType, owner);
            return this.program.instruction.depositVault(amount, {
                accounts: {
                    synthetic,
                    collateral,
                    reserveAddress,
                    userCollateralAccount,
                    owner,
                    state: this.stateAddress,
                    vaultEntry: vaultEntryAddress,
                    vault: vaultAddress,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                    assetsList: this.state.assetsList,
                    exchangeAuthority: this.exchangeAuthority
                }
            });
        });
    }
    vaultDepositTransaction(vaultDepositInstruction) {
        return __awaiter(this, void 0, void 0, function* () {
            const { owner, synthetic, collateral, userCollateralAccount, reserveAddress, amount, collateralToken, vaultType } = vaultDepositInstruction;
            const depositVaultIx = yield this.vaultDepositInstruction({
                owner,
                synthetic,
                collateral,
                userCollateralAccount,
                reserveAddress,
                amount,
                vaultType
            });
            const approveIx = spl_token_1.Token.createApproveInstruction(collateralToken.programId, userCollateralAccount, this.exchangeAuthority, owner, [], (0, utils_1.tou64)(amount));
            return new web3_js_1.Transaction().add(approveIx).add(depositVaultIx);
        });
    }
    vaultDeposit(depositVault) {
        return __awaiter(this, void 0, void 0, function* () {
            const tx = yield this.vaultDepositTransaction(depositVault);
            yield (0, utils_1.signAndSend)(tx, depositVault.signers, this.connection);
        });
    }
    borrowVaultInstruction({ owner, to, synthetic, collateral, collateralPriceFeed, amount, vaultType }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            const { vaultEntryAddress } = yield this.getVaultEntryAddress(synthetic, collateral, vaultType, owner);
            return this.program.instruction.borrowVault(amount, {
                accounts: {
                    synthetic,
                    collateral,
                    collateralPriceFeed,
                    owner,
                    to,
                    state: this.stateAddress,
                    vaultEntry: vaultEntryAddress,
                    vault: vaultAddress,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                    assetsList: this.state.assetsList,
                    exchangeAuthority: this.exchangeAuthority
                }
            });
        });
    }
    liquidateVaultInstruction({ owner, synthetic, collateral, collateralReserve, liquidationFund, collateralPriceFeed, amount, liquidator, liquidatorCollateralAccount, liquidatorSyntheticAccount, vaultType }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            const { vaultEntryAddress } = yield this.getVaultEntryAddress(synthetic, collateral, vaultType, owner);
            return this.program.instruction.liquidateVault(amount, {
                accounts: {
                    state: this.stateAddress,
                    assetsList: this.state.assetsList,
                    vaultEntry: vaultEntryAddress,
                    vault: vaultAddress,
                    collateralReserve,
                    liquidationFund,
                    synthetic,
                    collateral,
                    collateralPriceFeed,
                    liquidatorSyntheticAccount,
                    liquidatorCollateralAccount,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                    owner,
                    liquidator,
                    exchangeAuthority: this.exchangeAuthority
                }
            });
        });
    }
    updateVaultSyntheticPriceIx(synthetic) {
        return __awaiter(this, void 0, void 0, function* () {
            const syntheticStructure = this.assetsList.synthetics.find((s) => s.assetAddress.equals(synthetic));
            const syntheticFeedAddress = this.assetsList.assets[syntheticStructure.assetIndex].feedAddress;
            if (syntheticFeedAddress.equals(web3_js_1.PublicKey.default)) {
                return null;
            }
            const updateSyntheticPriceIx = yield this.updateSelectedPricesInstruction(this.state.assetsList, [syntheticFeedAddress]);
            return updateSyntheticPriceIx;
        });
    }
    borrowVaultTransaction(borrowVaultInstruction) {
        return __awaiter(this, void 0, void 0, function* () {
            const tx = new web3_js_1.Transaction();
            const updatePriceIx = yield this.updateVaultSyntheticPriceIx(borrowVaultInstruction.synthetic);
            if (updatePriceIx !== null) {
                tx.add(updatePriceIx);
            }
            const borrowIx = yield this.borrowVaultInstruction(borrowVaultInstruction);
            tx.add(borrowIx);
            return tx;
        });
    }
    borrowVault(borrowVault) {
        return __awaiter(this, void 0, void 0, function* () {
            const tx = yield this.borrowVaultTransaction(borrowVault);
            return yield (0, utils_1.signAndSend)(tx, borrowVault.signers, this.connection);
        });
    }
    withdrawVaultInstruction({ amount, owner, collateral, reserveAddress, collateralPriceFeed, synthetic, userCollateralAccount, vaultType }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            const { vaultEntryAddress } = yield this.getVaultEntryAddress(synthetic, collateral, vaultType, owner);
            const ix = this.program.instruction.withdrawVault(amount, {
                accounts: {
                    userCollateralAccount,
                    owner,
                    collateral,
                    collateralPriceFeed,
                    synthetic,
                    state: this.stateAddress,
                    vaultEntry: vaultEntryAddress,
                    vault: vaultAddress,
                    reserveAddress,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                    assetsList: this.state.assetsList,
                    exchangeAuthority: this.exchangeAuthority
                }
            });
            return ix;
        });
    }
    withdrawVaultTransaction(withdrawVaultTransaction) {
        return __awaiter(this, void 0, void 0, function* () {
            const tx = new web3_js_1.Transaction();
            const updatePriceIx = yield this.updateVaultSyntheticPriceIx(withdrawVaultTransaction.synthetic);
            if (updatePriceIx !== null) {
                tx.add(updatePriceIx);
            }
            const withdrawVaultIx = yield this.withdrawVaultInstruction(withdrawVaultTransaction);
            tx.add(withdrawVaultIx);
            return tx;
        });
    }
    repayVaultInstruction({ amount, owner, synthetic, collateral, userTokenAccountRepay, vaultType }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            const { vaultEntryAddress } = yield this.getVaultEntryAddress(synthetic, collateral, vaultType, owner);
            const ix = this.program.instruction.repayVault(amount, {
                accounts: {
                    owner,
                    synthetic,
                    collateral,
                    userTokenAccountRepay,
                    state: this.stateAddress,
                    vaultEntry: vaultEntryAddress,
                    vault: vaultAddress,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID,
                    assetsList: this.state.assetsList,
                    exchangeAuthority: this.exchangeAuthority
                }
            });
            return ix;
        });
    }
    repayVaultTransaction(repayVaultInstruction) {
        return __awaiter(this, void 0, void 0, function* () {
            const { amount, owner, synthetic, collateral, userTokenAccountRepay } = repayVaultInstruction;
            const approveIx = spl_token_1.Token.createApproveInstruction(spl_token_1.TOKEN_PROGRAM_ID, userTokenAccountRepay, this.exchangeAuthority, owner, [], (0, utils_1.tou64)(amount));
            const repayIx = yield this.repayVaultInstruction(repayVaultInstruction);
            return new web3_js_1.Transaction().add(approveIx).add(repayIx);
        });
    }
    repayVault(repayVault) {
        return __awaiter(this, void 0, void 0, function* () {
            const tx = yield this.repayVaultTransaction(repayVault);
            yield (0, utils_1.signAndSend)(tx, repayVault.signers, this.connection);
        });
    }
    setVaultHaltedInstruction({ halted, collateral, synthetic, vaultType }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            const ix = this.program.instruction.setVaultHalted(halted, {
                accounts: {
                    synthetic,
                    collateral,
                    state: this.stateAddress,
                    admin: this.state.admin,
                    vault: vaultAddress,
                    assetsList: this.state.assetsList,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID
                }
            });
            return ix;
        });
    }
    setVaultDebtInterestRateInstruction(debtInterestRate, { synthetic, collateral, vaultType }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            return this.program.instruction.setVaultDebtInterestRate(debtInterestRate, {
                accounts: {
                    synthetic,
                    collateral,
                    state: this.stateAddress,
                    admin: this.state.admin,
                    vault: vaultAddress
                }
            });
        });
    }
    setVaultLiquidationThresholdInstruction(liquidationThreshold, { synthetic, collateral, vaultType }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            return this.program.instruction.setVaultLiquidationThreshold(liquidationThreshold, {
                accounts: {
                    synthetic,
                    collateral,
                    state: this.stateAddress,
                    admin: this.state.admin,
                    vault: vaultAddress
                }
            });
        });
    }
    setVaultSetLiquidationRatioInstruction(liquidationRatio, { synthetic, collateral, vaultType }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            return this.program.instruction.setVaultSetLiquidationRatio(liquidationRatio, {
                accounts: {
                    synthetic,
                    collateral,
                    state: this.stateAddress,
                    admin: this.state.admin,
                    vault: vaultAddress
                }
            });
        });
    }
    setVaultLiquidationPenaltyLiquidatorInstruction(liquidationPenaltyLiquidator, { synthetic, collateral, vaultType }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            return this.program.instruction.setVaultLiquidationPenaltyLiquidator(liquidationPenaltyLiquidator, {
                accounts: {
                    synthetic,
                    collateral,
                    state: this.stateAddress,
                    admin: this.state.admin,
                    vault: vaultAddress
                }
            });
        });
    }
    setVaultLiquidationPenaltyExchangeInstruction(liquidationPenaltyExchange, { synthetic, collateral, vaultType }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            return this.program.instruction.setVaultLiquidationPenaltyExchange(liquidationPenaltyExchange, {
                accounts: {
                    synthetic,
                    collateral,
                    state: this.stateAddress,
                    admin: this.state.admin,
                    vault: vaultAddress
                }
            });
        });
    }
    setVaultMaxBorrowInstruction(maxBorrow, { synthetic, collateral, vaultType }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            return this.program.instruction.setVaultMaxBorrow(maxBorrow, {
                accounts: {
                    synthetic,
                    collateral,
                    state: this.stateAddress,
                    admin: this.state.admin,
                    vault: vaultAddress
                }
            });
        });
    }
    withdrawVaultAccumulatedInterestInstruction({ synthetic, collateral, to, amount, vaultType }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            return this.program.instruction.withdrawVaultAccumulatedInterest(amount, {
                accounts: {
                    synthetic,
                    collateral,
                    to,
                    assetsList: this.state.assetsList,
                    state: this.stateAddress,
                    admin: this.state.admin,
                    vault: vaultAddress,
                    exchangeAuthority: this.exchangeAuthority,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID
                }
            });
        });
    }
    withdrawVaultLiquidationPenaltyInstruction({ synthetic, collateral, liquidationFund, to, amount, vaultType }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            return this.program.instruction.withdrawVaultLiquidationPenalty(amount, {
                accounts: {
                    synthetic,
                    collateral,
                    to,
                    liquidationFund,
                    state: this.stateAddress,
                    admin: this.state.admin,
                    vault: vaultAddress,
                    exchangeAuthority: this.exchangeAuthority,
                    tokenProgram: spl_token_1.TOKEN_PROGRAM_ID
                }
            });
        });
    }
    updatePrices(assetsList) {
        return __awaiter(this, void 0, void 0, function* () {
            const assetsListData = yield this.getAssetsList(assetsList);
            const feedAddresses = assetsListData.assets
                .filter((asset) => !asset.feedAddress.equals(utils_1.DEFAULT_PUBLIC_KEY))
                .map((asset) => {
                return { pubkey: asset.feedAddress, isWritable: false, isSigner: false };
            });
            return yield this.program.rpc.setAssetsPrices({
                remainingAccounts: feedAddresses,
                accounts: {
                    assetsList: assetsList
                }
            });
        });
    }
    updatePricesInstruction(assetsList) {
        return __awaiter(this, void 0, void 0, function* () {
            const assetsListData = yield this.getAssetsList(assetsList);
            const feedAddresses = assetsListData.assets
                .filter((asset) => !asset.feedAddress.equals(utils_1.DEFAULT_PUBLIC_KEY))
                .map((asset) => {
                return { pubkey: asset.feedAddress, isWritable: false, isSigner: false };
            });
            return (yield this.program.instruction.setAssetsPrices({
                remainingAccounts: feedAddresses,
                accounts: {
                    assetsList: assetsList
                }
            }));
        });
    }
    updateSelectedPricesInstruction(assetsList, selectedAssets) {
        return __awaiter(this, void 0, void 0, function* () {
            const feedAddresses = selectedAssets.map((feedAddress) => {
                return { pubkey: feedAddress, isWritable: false, isSigner: false };
            });
            return (yield this.program.instruction.setAssetsPrices({
                remainingAccounts: feedAddresses,
                accounts: {
                    assetsList: assetsList
                }
            }));
        });
    }
    triggerVaultEntryDebtAdjustmentInstruction({ synthetic, collateral, owner, vaultType }) {
        return __awaiter(this, void 0, void 0, function* () {
            const { vaultAddress } = yield this.getVaultAddress(synthetic, collateral, vaultType);
            const { vaultEntryAddress } = yield this.getVaultEntryAddress(synthetic, collateral, vaultType, owner);
            return (yield this.program.instruction.triggerVaultEntryDebtAdjustment({
                accounts: {
                    synthetic,
                    collateral,
                    owner,
                    admin: this.state.admin,
                    state: this.stateAddress,
                    vault: vaultAddress,
                    vaultEntry: vaultEntryAddress,
                    assetsList: this.state.assetsList
                }
            }));
        });
    }
}
exports.Exchange = Exchange;
var PriceStatus;
(function (PriceStatus) {
    PriceStatus[PriceStatus["Unknown"] = 0] = "Unknown";
    PriceStatus[PriceStatus["Trading"] = 1] = "Trading";
    PriceStatus[PriceStatus["Halted"] = 2] = "Halted";
    PriceStatus[PriceStatus["Auction"] = 3] = "Auction";
})(PriceStatus = exports.PriceStatus || (exports.PriceStatus = {}));
var OracleType;
(function (OracleType) {
    OracleType[OracleType["Pyth"] = 0] = "Pyth";
    OracleType[OracleType["Chainlink"] = 1] = "Chainlink";
})(OracleType = exports.OracleType || (exports.OracleType = {}));
